import { Reveal, SectionHead, KineticLines } from "@/components/Motion";
import SEO from "@/components/SEO";
import Breadcrumbs from "@/components/Breadcrumbs";
import CTASection from "@/components/CTASection";
import Marquee from "@/components/Marquee";
import { IMAGES } from "@/config/images";
import useSettings from "@/lib/useSettings";

const PILLARS = [
  ["Vision", "To build a trusted Indian manufacturing brand serving pharmaceutical, healthcare and international markets."],
  ["Mission", "To design and manufacture reliable cleanroom equipment and precision-engineered products that meet the real requirements of controlled environments."],
  ["Engineering Approach", "Application-first design: understand the process, specify the materials and airflow, then engineer the product around it."],
  ["Manufacturing Philosophy", "Disciplined fabrication, honest materials and finishes, and inspection built into every stage of production."],
  ["Global Ambition", "To serve customers in India, the Middle East, Africa and beyond with export-ready engineering and documentation."],
];

export default function About() {
  const settings = useSettings();
  return (
    <div data-testid="about-page">
      <SEO title="About STJ Innovations" path="/about" image={IMAGES.hero}
        description="STJ Innovations (STJ Innovations LLP, New Delhi, India) is building a new generation of Indian manufacturing for pharmaceutical cleanroom equipment and precision engineering." />
      <section className="blueprint-dark bg-ink">
        <div className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8 lg:py-28">
          <Breadcrumbs dark items={[{ label: "About" }]} />
          <p className="eyebrow eyebrow-light">About STJ Innovations</p>
          <KineticLines
            lines={["Building a New Generation", "of Indian Manufacturing"]}
            className="mt-5 max-w-4xl font-display text-4xl font-medium leading-[1.06] tracking-tight text-white sm:text-5xl lg:text-6xl"
          />
        </div>
      </section>
      <Marquee />

      <section className="mx-auto max-w-7xl px-4 py-24 sm:px-6 lg:px-8 lg:py-32">
        <div className="grid gap-14 lg:grid-cols-12">
          <div className="lg:col-span-6">
            <Reveal>
              <p className="text-xl font-light leading-relaxed text-ink sm:text-2xl">
                STJ Innovations was founded with a vision to build high-quality pharmaceutical and cleanroom equipment in India.
              </p>
              <p className="mt-6 text-base leading-relaxed text-steel">
                With a foundation in engineering and industrial manufacturing, STJ aims to combine precision manufacturing, customization and modern technology to create reliable solutions for controlled environments.
              </p>
              <p className="mt-5 text-base leading-relaxed text-steel">
                Our long-term vision is to build a trusted Indian manufacturing brand serving pharmaceutical, healthcare and international markets — a company that international procurement teams can work with confidently, from first enquiry to delivered equipment.
              </p>
            </Reveal>
            <Reveal delay={0.1}>
              <dl className="mt-12 border-t border-line">
                {PILLARS.map(([term, desc], i) => (
                  <div key={term} className="grid gap-2 border-b border-line py-6 sm:grid-cols-3" data-testid={`about-pillar-${i}`}>
                    <dt className="font-mono text-[11px] uppercase tracking-[0.22em] text-accent">{term}</dt>
                    <dd className="text-sm leading-relaxed text-ink sm:col-span-2">{desc}</dd>
                  </div>
                ))}
              </dl>
            </Reveal>
          </div>
          <div className="lg:col-span-5 lg:col-start-8">
            <Reveal delay={0.1}>
              <div className="spotlight-img overflow-hidden border border-line">
                <img src={settings.img_about_primary || IMAGES.hero} alt="Cleanroom manufacturing environment" loading="lazy" className="aspect-[4/3] w-full object-cover" />
              </div>
              <div className="spotlight-img -mt-12 ml-auto w-3/4 overflow-hidden border border-line shadow-xl">
                <img src={settings.img_about_secondary || IMAGES.passbox} alt="Stainless steel cleanroom equipment detail" loading="lazy" className="aspect-[16/10] w-full object-cover" />
              </div>
            </Reveal>
            <Reveal delay={0.2}>
              <div className="mt-10 border border-line bg-mist p-7">
                <p className="eyebrow">Company Information</p>
                <dl className="mt-4 space-y-3 text-sm">
                  <div className="flex justify-between gap-4"><dt className="text-steel">Brand</dt><dd className="font-medium text-ink">STJ Innovations</dd></div>
                  <div className="flex justify-between gap-4"><dt className="text-steel">Legal entity</dt><dd className="text-right font-medium text-ink">STJ Innovations LLP</dd></div>
                  <div className="flex justify-between gap-4"><dt className="text-steel">Based in</dt><dd className="font-medium text-ink">India</dd></div>
                  <div className="flex justify-between gap-4"><dt className="text-steel">Focus</dt><dd className="text-right font-medium text-ink">Cleanroom equipment & precision engineering</dd></div>
                </dl>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="border-t border-line bg-mist">
        <div className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8">
          <SectionHead eyebrow="What We Manufacture" title="Focused Product Families"
            copy="Pass boxes, laminar air flow units, cleanroom doors and panels, cleanroom furniture, dispensing and sampling booths, pharmaceutical equipment, IoT-enabled solutions and precision sheet-metal components." />
        </div>
      </section>

      <CTASection />
    </div>
  );
}
